import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  useTheme,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import PageHeader from '../../components/common/PageHeader';
import { apiService } from '../../services/api';
import { formatCurrency } from '../../utils/helpers';

interface OverdueDebt {
  id: number;
  invoiceID: string;
  name: string;
  contactID: string;
  amountDue: number;
  dueDate: string;
  currencyCode: string;
}

type SortOption = 'daysOverdueDesc' | 'daysOverdueAsc' | 'amountDueDesc' | 'amountDueAsc';
type ViewMode = 'card' | 'list';

const Debts: React.FC = () => {
  const theme = useTheme();
  const [debts, setDebts] = useState<OverdueDebt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('daysOverdueDesc');
  const [viewMode, setViewMode] = useState<ViewMode>('card');

  useEffect(() => {
    fetchDebts();
  }, []);

  const fetchDebts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getOverdueDebts();
      setDebts(data);
    } catch (err) {
      console.error('Error fetching overdue debts:', err);
      setError('Failed to load overdue debts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateDaysOverdue = (dueDate: string): number => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = today.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleSortChange = (event: SelectChangeEvent<SortOption>) => {
    setSortBy(event.target.value as SortOption);
  };

  const getSortedDebts = () => {
    const sortedDebts = [...debts];
    
    switch (sortBy) {
      case 'daysOverdueDesc':
        sortedDebts.sort((a, b) => {
          const daysA = calculateDaysOverdue(a.dueDate);
          const daysB = calculateDaysOverdue(b.dueDate);
          return daysB - daysA; // Most overdue first
        });
        break;
      case 'daysOverdueAsc':
        sortedDebts.sort((a, b) => {
          const daysA = calculateDaysOverdue(a.dueDate);
          const daysB = calculateDaysOverdue(b.dueDate);
          return daysA - daysB; // Least overdue first
        });
        break;
      case 'amountDueDesc':
        sortedDebts.sort((a, b) => (b.amountDue || 0) - (a.amountDue || 0)); // Highest amount first
        break;
      case 'amountDueAsc':
        sortedDebts.sort((a, b) => (a.amountDue || 0) - (b.amountDue || 0)); // Lowest amount first
        break;
    }
    
    return sortedDebts;
  };

  const handleViewInXero = (invoiceID: string) => {
    const xeroUrl = `https://go.xero.com/app/!byhWs/invoicing/view/${invoiceID}`;
    window.open(xeroUrl, '_blank', 'noopener,noreferrer');
  };

  const getDaysOverdueColor = (days: number): string => {
    if (days > 60) return theme.palette.error.main;
    if (days > 30) return theme.palette.warning.main;
    return theme.palette.info.main;
  };

  if (isLoading) {
    return (
      <Box>
        <PageHeader
          title="Overdue Debts"
          subtitle="View and manage overdue invoices"
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader
          title="Overdue Debts"
          subtitle="View and manage overdue invoices"
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
          <Button onClick={fetchDebts} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  const sortedDebts = getSortedDebts();

  return (
    <Box>
      <PageHeader
        title="Overdue Debts"
        subtitle="View and manage overdue invoices"
      />

      {/* Summary */}
      <Box sx={{ mb: 4, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 250px' }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Total Overdue Invoices
              </Typography>
              <Typography variant="h4">
                {debts.length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px' }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Total Amount Due
              </Typography>
              <Typography variant="h4" color="error">
                {formatCurrency(debts.reduce((sum, debt) => sum + (debt.amountDue || 0), 0))}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* View Toggle and Sort Control */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        {/* View Toggle Buttons */}
        <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <IconButton
            size="small"
            onClick={() => setViewMode('card')}
            sx={{
              borderRadius: '4px 0 0 4px',
              backgroundColor: viewMode === 'card' ? 'primary.main' : 'transparent',
              color: viewMode === 'card' ? 'primary.contrastText' : 'text.secondary',
              '&:hover': {
                backgroundColor: viewMode === 'card' ? 'primary.dark' : 'action.hover',
              },
            }}
            title="Card View"
          >
            <GridViewIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setViewMode('list')}
            sx={{
              borderRadius: '0 4px 4px 0',
              backgroundColor: viewMode === 'list' ? 'primary.main' : 'transparent',
              color: viewMode === 'list' ? 'primary.contrastText' : 'text.secondary',
              '&:hover': {
                backgroundColor: viewMode === 'list' ? 'primary.dark' : 'action.hover',
              },
            }}
            title="List View"
          >
            <ViewListIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Sort Control */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="sort-label">Sort By</InputLabel>
          <Select
            labelId="sort-label"
            value={sortBy}
            label="Sort By"
            onChange={handleSortChange}
          >
            <MenuItem value="daysOverdueDesc">Days Overdue (Highest First)</MenuItem>
            <MenuItem value="daysOverdueAsc">Days Overdue (Lowest First)</MenuItem>
            <MenuItem value="amountDueDesc">Amount Due (Highest First)</MenuItem>
            <MenuItem value="amountDueAsc">Amount Due (Lowest First)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Debts Display - Card or List View */}
      {sortedDebts.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No overdue debts found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            All invoices are up to date!
          </Typography>
        </Card>
      ) : viewMode === 'card' ? (
        /* Card View */
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {sortedDebts.map((debt) => {
            const daysOverdue = calculateDaysOverdue(debt.dueDate);
            const daysColor = getDaysOverdueColor(daysOverdue);

            return (
              <Box key={debt.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)', lg: 'calc(25% - 18px)' } }}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: `4px solid ${daysColor}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Days Overdue Badge */}
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={`${daysOverdue} days overdue`}
                        size="small"
                        sx={{
                          backgroundColor: daysColor,
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </Box>

                    {/* Amount Due */}
                    <Typography variant="h5" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {formatCurrency(debt.amountDue || 0)}
                    </Typography>

                    {/* Due Date */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Due: {new Date(debt.dueDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Typography>

                    {/* Spacer */}
                    <Box sx={{ flexGrow: 1 }} />

                    {/* View in Xero Button */}
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      endIcon={<OpenInNewIcon />}
                      onClick={() => handleViewInXero(debt.invoiceID)}
                      sx={{ mt: 2 }}
                    >
                      View in Xero
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      ) : (
        /* List View */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Days Overdue</TableCell>
                <TableCell>Amount Due</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDebts.map((debt) => {
                const daysOverdue = calculateDaysOverdue(debt.dueDate);
                const daysColor = getDaysOverdueColor(daysOverdue);

                return (
                  <TableRow
                    key={debt.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={`${daysOverdue} days`}
                        size="small"
                        sx={{
                          backgroundColor: daysColor,
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {formatCurrency(debt.amountDue || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(debt.dueDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNewIcon />}
                        onClick={() => handleViewInXero(debt.invoiceID)}
                      >
                        View in Xero
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Debts;
